// contactsPermission.js
import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from './api'; // ← adaptez le chemin à votre projet


/**
 * useContactsPermission
 *
 * @param {boolean} allowContacts   - valeur courante depuis les settings utilisateur
 * @param {string}  token           - JWT pour l'appel API
 * @param {Function} onAllowContactsChange - callback pour mettre à jour le state parent
 *                                           ex: (val) => setAllowContacts(val)
 */
export const useContactsPermission = (allowContactsRef, token, onAllowContactsChange) => {
  const resolveRef = useRef(null);
  const [showDisclosure, setShowDisclosure] = useState(false);
  const { t } = useTranslation();
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
  // Sauvegarde allowContacts = true sur le serveur
  const saveAllowContacts = async () => {
    try {
      await api.put(
        `${API_URL}/api/profil/settings`,
        { allowContacts: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
	allowContactsRef.current = true;
      onAllowContactsChange?.(true);
    } catch (e) {
      console.error('Erreur mise à jour allowContacts', e);
    }
  };

  // Vérifie / demande la permission Android
  const doPermissionCheck = (resolve) => {
    const perms = window.cordova?.plugins?.permissions;
    if (!perms) {
      resolve(true); // web/desktop : pas de plugin, on laisse passer
      return;
    }
    perms.checkPermission(
      perms.READ_CONTACTS,
      (status) => {
        if (status.hasPermission) {
          resolve(true);
          return;
        }
        perms.requestPermission(
          perms.READ_CONTACTS,
          (result) => resolve(result.hasPermission),
          () => resolve(false)
        );
      },
      () => resolve(false)
    );
  };

  const requestContactsAccess = () => {
    return new Promise((resolve) => {

      if (allowContactsRef.current) {
        // Déjà autorisé côté serveur → on vérifie juste Android, pas de modal
        doPermissionCheck(resolve);
        return;
      }
	// ← Ne plus demander → on refuse silencieusement
    if (localStorage.getItem('contactsModalDismissed') === 'true') {
      resolve(false);
      return;
    }
      // Pas encore autorisé → modal de consentement d'abord
      resolveRef.current = async (accepted) => {
        setShowDisclosure(false);

        if (!accepted) {
          resolve(false);
          return;
        }

        // L'utilisateur a accepté la modal → on sauvegarde sur le serveur
        await saveAllowContacts();

        // Puis on demande la permission Android
        doPermissionCheck(resolve);
      };

      setShowDisclosure(true);
    });
  };

  const modal = showDisclosure ? (
  <div className="consent-modal">
    <div className="consent-content">
      <h2>{t('consentement.title')}</h2>
      <p>{t('consentement.titre1')}</p>
      <p>{t('consentement.titre2')}</p>
      <p>{t('consentement.titre3')}</p>
      <p>{t('consentement.titre4')}</p>
      <p>{t('consentement.titre5')}</p>
      <div className="buttons">
        <button style={{ fontSize: '0.8em' }}  onClick={() => resolveRef.current?.(true)}>
          {t('consentement.accepter')}
        </button>
        <button style={{ background: 'grey', fontSize: '0.8em' }} onClick={() => resolveRef.current?.(false)}>
          {t('consentement.ignorer')}
        </button>
        <button
  style={{
    background: 'none',
    border: '1px dashed rgba(128,128,128,0.4)',
    borderRadius: '8px',
    padding: '6px 12px',
    fontSize: '0.75em',
    color: 'var(--text-color)',
    opacity: 0.55,
    cursor: 'pointer',
    marginTop: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    width: '100%',
    justifyContent: 'center',
  }}
  onClick={() => {
    localStorage.setItem('contactsModalDismissed', 'true');
    resolveRef.current?.(false);
  }}
>
  <i className="ti ti-bell-off" style={{ fontSize: '14px' }} aria-hidden="true" />
  {t('consentement.ne_plus_demander')}
</button>
      </div>
    </div>
  </div>
) : null;

  return { requestContactsAccess, modal };
};
